import React from "react";
import {
  Box,
  Chip,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ImageUpload from "../../../common/ImageUpload";

import {
  cloneWorkoutBuilderConfig,
  WorkoutBuilderImageReference,
  WorkoutBuilderConfig as WorkoutBuilderConfigType,
} from "../../../../types/WorkoutBuilder";

const useStyles = makeStyles((theme) => ({
  rootPanel: {
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  summaryChips: {
    display: "flex",
    flexShrink: 0,
    "& > *": {
      marginLeft: theme.spacing(1),
    },
  },
  details: {
    display: "block",
  },
  detailsInner: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "& > * + *": {
      marginTop: theme.spacing(2),
    },
  },
  card: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  sectionTitle: {
    fontWeight: 600,
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
  },
  stepPanel: {
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
  },
  stepSummary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  stepSummaryMeta: {
    display: "flex",
    flexShrink: 0,
    "& > *": {
      marginLeft: theme.spacing(1),
    },
  },
  imageReferenceBox: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
}));

const listToString = (value?: string[]) =>
  Array.isArray(value) ? value.join(", ") : "";

const stringToList = (value: string) =>
  value
    .split(/[|,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const buildBodyAreaOptions = (
  imageReferences: WorkoutBuilderImageReference[] = []
) =>
  Array.from(
    new Set(
      imageReferences.flatMap((reference) => reference.options || [])
    )
  );

type Props = {
  value?: WorkoutBuilderConfigType;
  onChange: (nextValue: WorkoutBuilderConfigType) => void;
  disabled?: boolean;
  uploadContextId?: string;
};

const WorkoutBuilderConfig = ({
  value,
  onChange,
  disabled = false,
  uploadContextId = "draft-workout-builder",
}: Props) => {
  const classes = useStyles();
  const config = cloneWorkoutBuilderConfig(value);

  const updateConfig = (
    updater: (draft: WorkoutBuilderConfigType) => WorkoutBuilderConfigType
  ) => {
    onChange(updater(cloneWorkoutBuilderConfig(config)));
  };

  const updateImageReference = (
    stepIndex: number,
    referenceIndex: number,
    nextReference: WorkoutBuilderImageReference
  ) => {
    updateConfig((draft) => {
      const step = draft.steps[stepIndex];
      const imageReferences = [...(step.imageReferences || [])];
      imageReferences[referenceIndex] = nextReference;
      draft.steps[stepIndex] = {
        ...step,
        options:
          step.inputType === "body_area_list"
            ? buildBodyAreaOptions(imageReferences)
            : step.options,
        imageReferences,
      };
      return draft;
    });
  };

  const createImageField = (stepIndex: number, referenceIndex: number) => ({
    name: `workout-builder-step-${stepIndex}-image-${referenceIndex}`,
    value:
      config.steps[stepIndex]?.imageReferences?.[referenceIndex]?.imageURL || "",
  });

  const createImageForm = (stepIndex: number, referenceIndex: number) => ({
    setFieldValue: (_fieldName: string, value: string) => {
      const currentReference =
        config.steps[stepIndex]?.imageReferences?.[referenceIndex];

      if (!currentReference) return;

      updateImageReference(stepIndex, referenceIndex, {
        ...currentReference,
        imageURL: value,
      });
    },
  });

  const stepCount = config.steps.length;

  return (
    <ExpansionPanel defaultExpanded className={classes.rootPanel}>
      <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
        <Box className={classes.summaryRow}>
          <Box>
            <Typography variant="h6" className={classes.sectionTitle}>
              Workout Builder
            </Typography>
            <Typography variant="body2" className={classes.sectionDescription}>
              Configure the question flow people will see in the workout builder.
            </Typography>
          </Box>
          <Box className={classes.summaryChips}>
            <Chip
              size="small"
              variant="outlined"
              color={config.enabled ? "primary" : "default"}
              label={config.enabled ? "Enabled" : "Disabled"}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${stepCount} step${stepCount !== 1 ? "s" : ""}`}
            />
          </Box>
        </Box>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.details}>
        <Box className={classes.detailsInner}>
          <Paper className={classes.card}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!config.enabled}
                      disabled={disabled}
                      onChange={(event) =>
                        updateConfig((draft) => ({
                          ...draft,
                          enabled: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Builder Enabled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Build Button Label"
                  fullWidth
                  disabled={disabled}
                  value={config.buildButtonLabel || ""}
                  onChange={(event) =>
                    updateConfig((draft) => ({
                      ...draft,
                      buildButtonLabel: event.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>

          <Box>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Steps
            </Typography>
            <Typography variant="body2" className={classes.sectionDescription}>
              Each step stores the labels, prompt copy, options, and any image
              references needed by the frontend. Expand a step to edit it.
            </Typography>
          </Box>

          {config.steps.map((step, index) => (
            <ExpansionPanel key={step.key} className={classes.stepPanel}>
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Box className={classes.stepSummary}>
                  <Typography
                    variant="subtitle2"
                    className={classes.sectionTitle}
                  >
                    {step.title?.trim() || step.key}
                  </Typography>
                  <Box className={classes.stepSummaryMeta}>
                    <Chip size="small" variant="outlined" label={step.key} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        step.inputType === "body_area_list"
                          ? "Body Area List"
                          : "Single Select"
                      }
                    />
                  </Box>
                </Box>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Title"
                      fullWidth
                      disabled={disabled}
                      value={step.title || ""}
                      onChange={(event) =>
                        updateConfig((draft) => {
                          draft.steps[index] = {
                            ...draft.steps[index],
                            title: event.target.value,
                          };
                          return draft;
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Ionicon Name"
                      fullWidth
                      disabled={disabled}
                      value={step.iconName || ""}
                      onChange={(event) =>
                        updateConfig((draft) => {
                          draft.steps[index] = {
                            ...draft.steps[index],
                            iconName: event.target.value,
                          };
                          return draft;
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label="Input Type"
                      fullWidth
                      disabled={disabled}
                      value={step.inputType || "single_select"}
                      onChange={(event) =>
                        updateConfig((draft) => {
                          draft.steps[index] = {
                            ...draft.steps[index],
                            inputType: event.target.value as
                              | "single_select"
                              | "body_area_list",
                          };
                          return draft;
                        })
                      }
                    >
                      <MenuItem value="single_select">Single Select</MenuItem>
                      <MenuItem value="body_area_list">Body Area List</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Question"
                      fullWidth
                      disabled={disabled}
                      value={step.question || ""}
                      onChange={(event) =>
                        updateConfig((draft) => {
                          draft.steps[index] = {
                            ...draft.steps[index],
                            question: event.target.value,
                          };
                          return draft;
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Helper Text"
                      fullWidth
                      multiline
                      rows={2}
                      disabled={disabled}
                      value={step.helperText || ""}
                      onChange={(event) =>
                        updateConfig((draft) => {
                          draft.steps[index] = {
                            ...draft.steps[index],
                            helperText: event.target.value,
                          };
                          return draft;
                        })
                      }
                    />
                  </Grid>
                  {step.inputType !== "body_area_list" && (
                    <Grid item xs={12}>
                      <TextField
                        label="Options"
                        fullWidth
                        multiline
                        rows={2}
                        disabled={disabled}
                        helperText="Separate values with commas."
                        value={listToString(step.options)}
                        onChange={(event) =>
                          updateConfig((draft) => {
                            draft.steps[index] = {
                              ...draft.steps[index],
                              options: stringToList(event.target.value),
                            };
                            return draft;
                          })
                        }
                      />
                    </Grid>
                  )}
                  {step.inputType === "body_area_list" &&
                    (step.imageReferences || []).map(
                      (imageReference, referenceIndex) => (
                        <Grid
                          item
                          xs={12}
                          key={`${step.key}-${imageReference.id || referenceIndex}`}
                        >
                          <Box className={classes.imageReferenceBox}>
                            <Typography
                              variant="subtitle2"
                              className={classes.sectionTitle}
                              gutterBottom
                            >
                              Body Area Image Reference
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  label="Reference Label"
                                  fullWidth
                                  disabled={disabled}
                                  value={imageReference.label || ""}
                                  onChange={(event) =>
                                    updateImageReference(index, referenceIndex, {
                                      ...imageReference,
                                      label: event.target.value,
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={8}>
                                <TextField
                                  label="Image URL"
                                  fullWidth
                                  disabled={disabled}
                                  value={imageReference.imageURL || ""}
                                  onChange={(event) =>
                                    updateImageReference(index, referenceIndex, {
                                      ...imageReference,
                                      imageURL: event.target.value,
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  label="Options"
                                  fullWidth
                                  multiline
                                  rows={2}
                                  disabled={disabled}
                                  helperText="Separate values with commas."
                                  value={listToString(imageReference.options)}
                                  onChange={(event) =>
                                    updateImageReference(index, referenceIndex, {
                                      ...imageReference,
                                      options: stringToList(event.target.value),
                                    })
                                  }
                                />
                              </Grid>
                              {!disabled && (
                                <Grid item xs={12}>
                                  <ImageUpload
                                    field={createImageField(index, referenceIndex)}
                                    form={createImageForm(index, referenceIndex)}
                                    actionId={`${uploadContextId}/${step.key}/${imageReference.id}`}
                                    reference="workout-builder-images"
                                  />
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )
                    )}
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          ))}
        </Box>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
};

export default WorkoutBuilderConfig;
